import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useSearchParams,
	type LoaderFunctionArgs,
} from 'react-router'

import { getToast } from 'remix-toast'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { toast, Toaster } from 'sonner'
import { track, resetTracking } from '#app/lib/analytics/track.client.ts'
import { PHProvider } from '#app/lib/analytics/tracking-provider.tsx'
import { getUser } from '#app/lib/firebase/auth'
import { getFirestoreInstance } from '#app/lib/firebase/firebase.server'
import { getEnv } from '#app/utils/env.server'
import { honeypot } from '#app/utils/honeypot.server'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { NavigationDock } from './components/navigation-dock.tsx'
import { ShareDrawer } from './components/ui/share-drawer.tsx'

import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { pipeHeaders } from './utils/headers.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'

import { makeTimings } from './utils/timing.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		// {
		//   rel: "preconnect",
		//   href: "https://fonts.gstatic.com",
		//   crossOrigin: "anonymous",
		// },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
		},
	].filter(Boolean)
}

// Root meta - child routes handle specific meta tags
export const meta: Route.MetaFunction = () => {
	return [] // All routes define their own meta tags
}

export async function loader(args: LoaderFunctionArgs) {
	const { request } = args
	const timings = makeTimings('root loader')

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	// Get user authentication state
	const { user, headers: authHeaders } = await getUser(request)

	// Check if user has a profile and fetch profile data (with caching)
	let hasProfile = false
	let userProfile: {
		city_name: string
		x_handle?: string
		avatar_url?: string
		display_name?: string
	} | null = null
	if (user) {
		try {
			const { getCachedUserProfile } = await import('#app/lib/cache.server')

			const cached = await getCachedUserProfile(
				user.uid,
				async () => {
					const db = getFirestoreInstance()
					const profileSnapshot = await db
						.collection('profiles')
						.where('user_id', '==', user.uid)
						.limit(1)
						.get()

					const hasProfile = !profileSnapshot.empty
					let userProfile: {
						city_name: string
						x_handle?: string
						avatar_url?: string
						display_name?: string
					} | null = null

					// Extract profile data if exists
					if (!profileSnapshot.empty && profileSnapshot.docs[0]) {
						const profileDoc = profileSnapshot.docs[0]
						const profileData = profileDoc.data()
						userProfile = {
							city_name: profileData.city_name || '',
							x_handle: profileData.x_handle || '',
							avatar_url:
								profileData.profile_image_url || profileData.avatar_url || '',
							display_name: profileData.display_name || '',
						}
					}

					return { hasProfile, userProfile }
				},
				timings,
			)

			hasProfile = cached.hasProfile
			userProfile = cached.userProfile
		} catch (error) {
			console.error('Error checking profile status:', error)
		}
	}

	return {
		user,
		hasProfile,
		userProfile,
		toast,
		honeyProps,
		requestInfo: {
			hints: getHints(request),
			origin: getDomainUrl(request),
			path: new URL(request.url).pathname,
		},
		ENV: getEnv(),
		headers: combineHeaders(
			{ 'Server-Timing': timings.toString() },
			toastHeaders,
			authHeaders,
		),
	}
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className="h-full overflow-x-hidden">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()

	return (
		<Document nonce={nonce} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const location = useLocation()
	const {
		toast: toastData,
		user,
		hasProfile,
		userProfile,
	} = useLoaderData<typeof loader>()
	const [searchParams, setSearchParams] = useSearchParams()

	const isSignedIn = !!user
	const userId = user?.uid

	// Share drawer state
	const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false)
	const [shareDrawerCity, setShareDrawerCity] = useState('')

	// Detect profile creation success and show share drawer
	useEffect(() => {
		const profileCreated = searchParams.get('profileCreated')
		const city = searchParams.get('city')

		if (profileCreated === 'true' && city) {
			setShareDrawerCity(city)
			setIsShareDrawerOpen(true)

			// Clean up URL params
			const newParams = new URLSearchParams(searchParams)
			newParams.delete('profileCreated')
			newParams.delete('city')
			setSearchParams(newParams, { replace: true })
		}
	}, [searchParams, setSearchParams])

	useEffect(() => {
		if (toastData) {
			// Use Sonner's toast API with type-specific methods
			const toastType = toastData.type || 'message'
			if (toastType === 'success') {
				toast.success(toastData.message)
			} else if (toastType === 'error') {
				toast.error(toastData.message)
			} else if (toastType === 'info') {
				toast.info(toastData.message)
			} else if (toastType === 'warning') {
				toast.warning(toastData.message)
			} else {
				toast(toastData.message)
			}
		}
	}, [toastData])

	// Track pageviews on route changes
	useEffect(() => {
		track('$pageview')
	}, [location])

	// User identification and reset tracking
	useEffect(() => {
		// Check the sign in status and user info,
		// and identify the user if they aren't already
		if (isSignedIn && userId && user && !posthog._isIdentified()) {
			posthog.identify(userId, {
				email: user.email || undefined,
				name: userProfile?.display_name || user.displayName || undefined,
				avatar_url: userProfile?.avatar_url || user.photoURL || undefined,
				twitter_handle: userProfile?.x_handle || undefined,
				city: userProfile?.city_name || undefined,
			})
		}

		// Reset the user if they sign out
		if (!isSignedIn && posthog._isIdentified()) {
			resetTracking()
		}
	}, [isSignedIn, userId, user, userProfile])

	// Handler for share button click from navigation dock
	const handleShareClick = (city?: string) => {
		setShareDrawerCity(city || '')
		setIsShareDrawerOpen(true)
	}

	return (
		<>
			<Outlet />
			<NavigationDock
				user={user}
				hasProfile={hasProfile}
				userCity={userProfile?.city_name}
				onShareClick={handleShareClick}
			/>
			<ShareDrawer
				open={isShareDrawerOpen}
				onOpenChange={setIsShareDrawerOpen}
				cityName={shareDrawerCity}
			/>
			<Toaster position="top-right" theme="dark" />
		</>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<PHProvider>
				<App />
			</PHProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

export const ErrorBoundary = GeneralErrorBoundary
